import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Printer, Loader2 } from 'lucide-react';
import { formatDateFR } from '../../utils/dateFormat';
import { API, serviceLabel } from '../../utils/kitchenTasks';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const PRINTABLE = ['a_faire', 'en_cours', 'terminee', 'a_valider', 'validee', 'refusee', 'non_realisee'];
const norm = (s) => (s || '').trim().toLowerCase();

const PrintPrepDialog = ({ open, onOpenChange, stations, allItems, localPrep, selectedDate, printStation, setPrintStation, checkedItems }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/kitchen-tasks`, { params: { date: selectedDate } })
      .then(res => { if (!cancelled) setTasks((res.data.data || []).filter(t => PRINTABLE.includes(t.status) && !t.archived)); })
      .catch(() => { if (!cancelled) setTasks([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, selectedDate]);

  // Rows per station: saved kitchen tasks first, then legacy checked items not already covered by a task
  const rowsByStation = useMemo(() => {
    const out = {};
    stations.forEach(s => {
      const taskRows = tasks.filter(t => t.category_name === s.id).map(t => ({
        key: `t-${t.id}`, name: t.title, qty: t.planned_quantity != null ? `${t.planned_quantity} ${t.unit || ''}`.trim() : '',
        who: (t.assigned_user_names || []).join(', '), note: [t.due_time ? `avant ${t.due_time}` : '', t.service && t.service !== 'journee' ? serviceLabel(t.service) : '', t.description || ''].filter(Boolean).join(' · '),
        done: ['terminee', 'validee'].includes(t.status),
      }));
      const covered = new Set(taskRows.map(r => norm(r.name)));
      const legacyRows = (allItems[s.id] || []).filter(i => checkedItems?.has(i.id) && !covered.has(norm(i.name))).map(i => {
        const p = localPrep[i.id] || {};
        return { key: `i-${i.id}`, name: i.name, qty: p.quantity ? `${p.quantity} ${p.unit || i.unit || ''}`.trim() : '', who: (p.assigned_to_names || []).join(', '), note: p.note || '', done: false };
      });
      out[s.id] = [...taskRows, ...legacyRows];
    });
    return out;
  }, [tasks, stations, allItems, localPrep, checkedItems]);

  const visibleStations = stations.filter(s => printStation === 'all' || printStation === s.id);
  const total = visibleStations.reduce((n, s) => n + rowsByStation[s.id].length, 0);
  const hasPrintable = total > 0;

  const handlePrint = () => {
    const content = document.getElementById('print-content');
    if (!content) return;
    let iframe = document.getElementById('print-iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'print-iframe';
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;';
      document.body.appendChild(iframe);
    }
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write('<!DOCTYPE html><html><head><title>Mise en Place</title>');
    doc.write('<style>*{font-family:"Helvetica Neue",Arial,sans-serif;margin:0}body{padding:20px}table{width:100%;border-collapse:collapse}th,td{padding:4px 6px;text-align:left;vertical-align:top}th{border-bottom:1px solid #999}td{border-bottom:1px solid #ddd}.box{width:14px;height:14px;border:2px solid #666;border-radius:3px;display:inline-block}.box.done{background:#666}@media print{body{padding:10px}}</style>');
    doc.write('</head><body>');
    doc.write(content.innerHTML);
    doc.write('</body></html>');
    doc.close();
    setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white border-gray-200 max-h-[90vh] overflow-y-auto" data-testid="print-prep-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-gray-900 flex items-center gap-2">
            <Printer size={20} className="text-neon-pink" />Imprimer la mise en place
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="print-loading"><Loader2 size={16} className="animate-spin" />Chargement des tâches enregistrées…</div>
        ) : !hasPrintable && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm" data-testid="print-empty">
            Aucune tâche enregistrée pour cette date{printStation !== 'all' ? ' sur ce poste' : ''}. Sélectionnez des items puis « Enregistrer » pour créer la mise en place.
          </div>
        )}
        <div className="flex gap-2 flex-wrap mb-4">
          <button onClick={() => setPrintStation('all')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${printStation === 'all' ? 'bg-neon-pink/10 text-neon-pink border border-neon-pink/30' : 'bg-gray-100 text-gray-500 border border-gray-200'}`} data-testid="print-station-all">
            Tous les postes
          </button>
          {stations.map(s => (
            <button key={s.id} onClick={() => setPrintStation(s.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${printStation === s.id ? `${s.bg} ${s.color} border ${s.border}` : 'bg-gray-100 text-gray-500 border border-gray-200'}`} data-testid={`print-station-${s.label}`}>
              {s.label} {rowsByStation[s.id].length > 0 && <span className="opacity-70">({rowsByStation[s.id].length})</span>}
            </button>
          ))}
        </div>
        <div className="bg-white text-black rounded-xl p-6 border border-gray-200" id="print-content">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold">Mise en Place</h2>
              <p className="text-gray-500">{formatDateFR(selectedDate)}{hasPrintable ? ` · ${total} tâche${total > 1 ? 's' : ''}` : ''}</p>
            </div>
          </div>
          {visibleStations.map(station => {
            const rows = rowsByStation[station.id];
            if (rows.length === 0) return null;
            return (
              <div key={station.id} className="mb-4" data-testid={`print-section-${station.label}`}>
                <h3 className="font-bold text-lg border-b-2 border-black pb-1 mb-2">{station.label}</h3>
                <table className="w-full border-collapse text-sm mb-2">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-1 w-8">OK</th>
                      <th className="text-left py-1">Tâche</th>
                      <th className="text-left py-1 w-24">Quantité</th>
                      <th className="text-left py-1 w-36">Qui</th>
                      <th className="text-left py-1 w-40">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.key} className="border-b border-gray-200">
                        <td className="py-1.5"><span className={`box inline-block w-3.5 h-3.5 border-2 border-gray-500 rounded ${r.done ? 'done bg-gray-600' : ''}`} /></td>
                        <td className="py-1.5 font-medium">{r.name}</td>
                        <td className="py-1.5 text-xs">{r.qty}</td>
                        <td className="py-1.5 text-xs text-gray-600">{r.who}</td>
                        <td className="py-1.5 text-xs text-gray-600">{r.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
          <div className="mt-4 pt-3 border-t border-gray-300 text-xs text-gray-400 text-center">Chef's Connect</div>
        </div>
        <div className="flex gap-3 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-full bg-gray-100 border-gray-200 text-gray-700" data-testid="print-close-btn">
            Fermer
          </Button>
          <Button onClick={handlePrint} disabled={!hasPrintable || loading} className="flex-1 bg-neon-pink hover:bg-neon-pink/90 rounded-full shadow-neon-pink" data-testid="confirm-print-btn">
            <Printer size={16} className="mr-2" />Imprimer ({total})
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrintPrepDialog;
